import React, { useEffect, useRef, useState, useMemo } from "react";
import "../styles/user_management_styles.css";
import Navbar from "./Navbar.js";
import "bootstrap/dist/css/bootstrap.min.css";
import Table from "./Table.js";
import useFetch from "./useFetch.js";
import swal from "sweetalert";
import axios from "axios";
import config from "../config.js";
import Cookie from "universal-cookie";
import { makeStyles } from "@mui/styles";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Box,
} from "@mui/material";
import mixpanel from "mixpanel-browser";

const useStyles = makeStyles(() => ({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "250px",
    overflowY: "auto",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    marginTop: "15px",
  },
}));

const Views_Create = () => {
  const cookies = new Cookie();
  let userToken = "";
  let actionInitiatingUser = -1;
  try {
    userToken = cookies.get("userToken") || "";
    actionInitiatingUser = parseInt(cookies.get("userID")) || -1;
  } catch {}

  const headers = {
    Authorization: `Bearer ${userToken}`,
    "Content-Type": "application/json",
  };

  const classes = useStyles();
  let selectedRows = useRef([]);
  let tempStartDateTime = useRef("");
  let tempEndDateTime = useRef("");

  const {
    data: sources,
    isPending,
    error,
  } = useFetch({
    actionInitiatingUser: { userID: actionInitiatingUser.toString() },
    URL: config.BACKEND_URL + "/logger_sources/sources",
  });

  const [userGroup, setUserGroup] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [stateOption, setStateOption] = useState([]);

  const tableData = useMemo(() => sources?.data || [], [sources]);

  const tableColumns = useMemo(() => {
    const columns = sources?.columns || [];
    return columns.map((column) => {
      if (column.field === "data") {
        return {
          ...column,
          title: "Topic",
          formatter: (cell) => {
            const v = cell.getValue();
            if (v && typeof v === "object") return v.topic || JSON.stringify(v);
            return String(v || "N/A");
          },
          width: 200,
          resizable: true,
        };
      }
      if (column.field === "createdAt" || column.field === "CREATEDAT") {
        return {
          ...column,
          title: "Created At",
          formatter: (cell) => new Date(cell.getValue()).toLocaleString(),
        };
      }
      if (column.field === "updatedAt" || column.field === "UPDATEDAT") {
        return {
          ...column,
          title: "Updated At",
          formatter: (cell) => new Date(cell.getValue()).toLocaleString(),
        };
      }
      return { ...column, width: column.width || 150, resizable: true };
    });
  }, [sources]);

  const handleStartTime = (e) => {
    tempStartDateTime.current = e.target.value;
    if (!tempStartDateTime.current)
      swal("Start time cannot be unspecified!", "", "error");
    else if (!areDatesValid(tempStartDateTime.current, tempEndDateTime.current))
      swal("Start time must be before end time!", "", "error");
  };

  const handleEndTime = (e) => {
    tempEndDateTime.current = e.target.value;
    if (!tempEndDateTime.current)
      swal("End time cannot be unspecified!", "", "error");
    else if (!areDatesValid(tempStartDateTime.current, tempEndDateTime.current))
      swal("End time must be after start time!", "", "error");
  };

  const areDatesValid = (a = "", b = "") => {
    if (!a || !b) return true;
    return new Date(a) <= new Date(b);
  };

  const handleViewsSubmit = async (e) => {
    e.preventDefault();
    mixpanel.track("View Create Button Clicked", {
      userID: cookies.get("userID"),
    });

    if (!tempStartDateTime.current || !tempEndDateTime.current) {
      swal("Start and end time required!", "", "error");
      return;
    }

    if (!areDatesValid(tempStartDateTime.current, tempEndDateTime.current)) {
      swal("Invalid date range!", "", "error");
      return;
    }

    if (!stateOption.some((x) => x)) {
      swal("Select at least one User Group", "", "error");
      return;
    }

    let name = "",
      tableStatus = false,
      chartStatus = false;

    for (let ele of e.target) {
      if (ele.nodeName === "INPUT") {
        if (ele.id === "name") name = ele.value;
        if (ele.id === "table") tableStatus = ele.checked;
        if (ele.id === "chart") chartStatus = ele.checked;
      }
    }

    const sourceIDs = selectedRows.current.map((r) => r.id);
    if (sourceIDs.length === 0) {
      swal("No sources selected!", "", "error");
      return;
    }

    const filteredUserGroup = userGroup.filter((_, i) => stateOption[i]);

    const body = {
      timeStamps: {
        start: tempStartDateTime.current,
        end: tempEndDateTime.current,
        offsetMinutes: new Date().getTimezoneOffset(),
      },
      sourceIDs,
      name,
      table: tableStatus,
      chart: chartStatus,
      filteredUserGroup,
      actionInitiatingUser: actionInitiatingUser.toString(),
    };

    axios
      .post(config.BACKEND_URL + `/views`, body, { headers })
      .then(() => {
        swal("Stored the view!", "", "success");
        setTimeout(() => window.location.reload(), 500);
      })
      .catch((err) => {
        if (err.response?.data?.name === "ViewAlreadyExists")
          swal("View name exists!", "Choose another name.", "error");
        else swal("Error storing view!", "", "error");
      });
  };

  const handleChangeDialog = (e, index) => {
    const arr = [...stateOption];
    arr[index] = e.target.checked;
    setStateOption(arr);
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleClear = () => setStateOption(Array(userGroup.length).fill(false));
  const handleAll = () => setStateOption(Array(userGroup.length).fill(true));

  useEffect(() => {
    axios
      .post(
        config.BACKEND_URL + "/views/user-group",
        { userId: actionInitiatingUser.toString() },
        { headers }
      )
      .then((res) => {
        const ugs = res.data.map((ug) => ({ id: ug.id, name: ug.name }));
        setUserGroup(ugs);
        setStateOption(Array(ugs.length).fill(true));
      });
  }, []);

  const dialogContent = useMemo(() => {
    return userGroup.map((ug, index) => (
      <FormControlLabel
        key={ug.id}
        control={
          <Checkbox
            checked={stateOption[index] || false}
            onChange={(e) => handleChangeDialog(e, index)}
            color="primary"
          />
        }
        label={ug.name}
      />
    ));
  }, [userGroup, stateOption]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #1e293b 0%, #020617 45%, #020617 100%)",
        color: "white",
        display: "block",
      }}
    >
      <style>{`
                /* Ensure no distinction between background layers */
                body, html {
                    margin: 0;
                    padding: 0;
                    background-color: #020617 !important;
                }
                .mqtt-card {
                    background: rgba(15, 23, 42, 0.4); /* Lower opacity to blend better */
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 18px;
                    padding: 28px;
                    backdrop-filter: blur(14px);
                    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
                    margin-bottom: 30px;
                }
                .mqtt-title {
                    color: #f8fafc;
                    font-weight: 700;
                    text-align: center;
                    letter-spacing: 0.5px;
                }
                .mqtt-label {
                    color: #94a3b8;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    font-weight: 600;
                    margin-bottom: 8px;
                    display: block;
                }
                input[type=datetime-local], 
                input[type=text] {
                    background: rgba(2, 6, 23, 0.6) !important;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #f8fafc;
                    border-radius: 8px;
                    padding: 12px;
                    width: 100%;
                }
                input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 1px #3b82f6;
                }
                .mqtt-btn {
                    background: #3b82f6;
                    border: none;
                    padding: 10px 32px;
                    border-radius: 8px;
                    color: white;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .mqtt-btn:hover {
                    background: #2563eb;
                    transform: translateY(-1px);
                }
                .mqtt-btn-secondary {
                    background: #ef4444;
                }
                .mqtt-btn-secondary:hover {
                    background: #dc2626;
                }
            `}</style>

      <Navbar />

      <Box sx={{ px: { xs: 2, md: 6 }, py: 12 }}>
        <header className="mqtt-title h2 mb-4">Create New View</header>

        <div className="row d-flex justify-content-center">
          <div className="col-xl-10 col-lg-11 mqtt-card">
            <form id="createForm" onSubmit={handleViewsSubmit}>
              {/* Date range */}
              <section className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="mqtt-label">Start Date & Time</label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    onChange={handleStartTime}
                  />
                </div>

                <div className="col-md-6">
                  <label className="mqtt-label">End Date & Time</label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    onChange={handleEndTime}
                  />
                </div>
              </section>

              {/* Name */}
              <section className="mb-4">
                <label className="mqtt-label">View Identifier Name</label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder="e.g. Daily Operations Log"
                />
              </section>

              {/* Table + Chart Toggle */}
              <section
                className="text-center mb-4 p-3 rounded"
                style={{ background: "rgba(0,0,0,0.2)" }}
              >
                <div className="mqtt-label mb-3">
                  Include Visualization Components
                </div>
                <div className="d-flex justify-content-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="table"
                        sx={{
                          color: "rgba(255,255,255,0.3)",
                          "&.Mui-checked": { color: "#3b82f6" },
                        }}
                      />
                    }
                    label="Data Table"
                    sx={{ ".MuiTypography-root": { fontSize: "0.9rem" } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="chart"
                        sx={{
                          color: "rgba(255,255,255,0.3)",
                          "&.Mui-checked": { color: "#3b82f6" },
                        }}
                      />
                    }
                    label="Analytics Chart"
                    sx={{ ".MuiTypography-root": { fontSize: "0.9rem" } }}
                  />
                </div>
              </section>

              {/* User Groups Access */}
              <section className="d-flex align-items-center justify-content-between mb-4">
                <span className="mqtt-label m-0">Access Management</span>
                <Button
                  onClick={handleOpenDialog}
                  variant="outlined"
                  sx={{
                    color: "#3b82f6",
                    borderColor: "rgba(59, 130, 246, 0.5)",
                    "&:hover": {
                      borderColor: "#3b82f6",
                      background: "rgba(59, 130, 246, 0.05)",
                    },
                  }}
                >
                  Select User Groups
                </Button>
              </section>

              {/* Data Sources Table Section */}
              <Box className={classes.tableContainer}>
                <div className="mqtt-label mb-2">Select Data Sources</div>
                {isPending && (
                  <div className="text-center py-4 text-muted">
                    Fetching sources...
                  </div>
                )}
                {error && (
                  <div className="text-center py-4 text-danger">
                    Unable to load data sources.
                  </div>
                )}

                {sources && sources.columns?.length > 0 && (
                  <Table
                    tableColumns={tableColumns}
                    tableData={tableData}
                    onSelectionChange={(rows) => (selectedRows.current = rows)}
                    shouldModifySources={false}
                    tableID="sourcesTable"
                    shouldDownload={false}
                    isSelectable={true}
                    layout="fitDataFill"
                  />
                )}
              </Box>

              {/* Form Action Buttons */}
              <section className="d-flex justify-content-center gap-3 mt-5">
                <button type="submit" className="mqtt-btn">
                  Create View
                </button>
                <button
                  type="reset"
                  className="mqtt-btn mqtt-btn-secondary"
                  onClick={() => window.location.reload()}
                >
                  Discard
                </button>
              </section>
            </form>
          </div>
        </div>
      </Box>

      {/* Dialog Styling override for dark theme */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: "#0f172a",
            color: "white",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          Assign Access Groups
        </DialogTitle>
        <DialogContent className={classes.dialogContent} sx={{ py: 2 }}>
          {dialogContent}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: "1px solid rgba(255,255,255,0.1)", p: 2 }}
        >
          <Button onClick={handleCloseDialog} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button onClick={handleClear} sx={{ color: "#f43f5e" }}>
            Clear All
          </Button>
          <Button onClick={handleAll} sx={{ color: "#10b981" }}>
            Select All
          </Button>
          <Button onClick={handleCloseDialog} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Views_Create;
